import { DefaultFormFields, FormControl, FormController } from '@insurancemenu/imenu-components-lib/dist/utils/forms';
import { makeAutoObservable, runInAction } from 'mobx';
import { OptionType } from '@insurancemenu/imenu-components-lib/dist/types';
import { getAlliedAgencies, getAlliedAgents } from 'api/operations/idTrackerService';
import { Agent, AlliedAgency } from 'types/idTrackerService';
import { BrowsingCriteria } from '@insurancemenu/imenu-components-lib/dist/types/productCardsTypings';
import { AlliedRegion } from './domain-data/alliedStateRegionMap';

export interface TinAndGaFormFields extends DefaultFormFields {
  tin: FormControl<string>;
  ga: FormControl<string>;
}

interface ConstructorParams {
  setAvailableAgents: (agents: Agent[]) => void;
}

export class TinAndGaDelegate {
  private _setAvailableAgents: (agents: Agent[]) => void;
  private _aorRegion: AlliedRegion | null = null;
  private _agencies: AlliedAgency[] = [];

  public form = new FormController<TinAndGaFormFields>({
    tin: new FormControl<string>('', {
      customValidations: [
        {
          check: (value: string): boolean => /^\d{2}-\d{7}$/.test(value) || /^\d{3}-\d{2}-\d{4}$/.test(value),
          msg: 'TIN must be formatted as XX-XXXXXXX or XXX-XX-XXXX',
        },
      ],
    }),
    ga: new FormControl<string>(''),
  });

  public get isFormValid(): boolean {
    return this.form.basicFieldsValidationsPassed;
  }

  public get aorRegion(): AlliedRegion | null {
    return this._aorRegion;
  }

  public get gaOptions(): OptionType[] {
    return this._agencies.map((agency) => ({
      value: agency.id,
      label: agency.name,
    }));
  }

  public get gaName(): string | undefined {
    return this.gaOptions.find((ga) => ga.value === this.form.fields.ga.value)?.label;
  }

  public setAorRegion(aorRegion: AlliedRegion): void {
    this._aorRegion = aorRegion;
  }

  public async loadAgencies(bc: BrowsingCriteria[]): Promise<void> {
    const agencies = await getAlliedAgencies(bc);
    runInAction(() => {
      this._agencies = agencies;
    });
  }

  public presetTin(tinValue: string): void {
    this.form.fields.tin.overrideValue(tinValue);
  }

  public async requestAvailableAgents(): Promise<void> {
    this.form.setLoading(true);
    await runInAction(async () => {
      const { ga, tin } = this.form.getFormDataObject();
      await getAlliedAgents(this._aorRegion as string, ga as string, tin as string).then((agents) => {
        runInAction(() => {
          this._setAvailableAgents(agents);
        });
      });
      runInAction(() => {
        this.form.setLoading(false);
      });
    });
  }

  public async populateTinAndGa(agentData: Agent): Promise<void> {
    this.form.populateFields({
      tin: agentData.tin,
      ga: agentData.gaNumber,
    });
    await this.requestAvailableAgents();
  }

  constructor({ setAvailableAgents }: ConstructorParams) {
    makeAutoObservable(this, {}, { autoBind: true });

    this._setAvailableAgents = setAvailableAgents;
  }
}
